'use client';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, CheckSquare, Type, Upload, Copy, FilePlus, RotateCcw, AlertCircle, X } from 'lucide-react';
import { StandardButton, SecondaryButton, DeleteButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { GenericForm, type GenericFormField } from '@/styles/GenericForm';
import { GenericConfirmDialog } from '@/styles/GenericConfirmDialog';
import { useUser } from '@/contexts/UserContext';
import { colors } from '@/design-system/colors';
import '@/design-system/typography.css';
import '@/design-system/forms.css';
import { manualComparisonService } from '../services/manual-comparison-service';
import { pdfAPIService } from '../services/pdf-api-service';
import { extractEstimateData, type ExtractedEstimateData } from '../services/sheet-extraction-service';
import type { ManualComparison, TableRow, TableColumn } from '../types';
import { useProject } from '@/components/ProjectSelector';

const FIRST_COLUMN_REM = 14;
const COLUMN_REM = 13;

const defaultSheet = (): { columns: TableColumn[]; rows: TableRow[] } => ({
  columns: [
    { id: 'col-1', subcontractorName: 'Vendor 1', total: '' },
    { id: 'col-2', subcontractorName: 'Vendor 2', total: '' },
  ],
  rows: [1, 2].map((n) => ({
    id: `row-${n}`,
    itemName: `Item ${n}`,
    cells: [1, 2].map((c) => ({ id: `cell-${n}-${c}`, value: '', type: 'text' as const })),
  })),
});

const SHEET_NAME_FIELDS: GenericFormField[] = [
  { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'e.g. Concrete bids - Kitchen Remodel', colSpan: 2 },
];

const AUTOSAVE_MS = 2000;
const DRAFT_KEY_PREFIX = 'krewup.bids.sheet-draft.';

interface SheetDraft {
  sheetId: string | null;
  sheetName: string;
  sheetProjectId: string | null;
  columns: TableColumn[];
  rows: TableRow[];
}

// localStorage can be missing / full / blocked: the draft is a safety net, never a requirement
const readDraft = (key: string): SheetDraft | null => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || 'null');
    return parsed && Array.isArray(parsed.columns) && Array.isArray(parsed.rows) ? parsed : null;
  } catch {
    return null;
  }
};
const writeDraft = (key: string, draft: SheetDraft) => {
  try { localStorage.setItem(key, JSON.stringify(draft)); } catch { /* best effort */ }
};
const clearDraft = (key: string) => {
  try { localStorage.removeItem(key); } catch { /* best effort */ }
};

/** Which edges of a horizontally scrolling area still hide content -- drives the edge shadows that say "there is more". */
function useScrollEdges(dependency: unknown) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setEdges({ left: el.scrollLeft > 1, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1 });
  }, []);
  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, dependency]);
  return { ref, edges, onScroll: measure };
}

interface ComparisonSheetProps {
  /** A saved sheet picked in the "Saved Sheets" tab: loaded once when this tab opens. */
  openSheetId?: string | null;
  onSheetOpened?: () => void;
}

export function ComparisonSheet({ openSheetId, onSheetOpened }: ComparisonSheetProps) {
  const { companyId } = useUser();
  // A sheet always belongs to a project (bid_comparisons.project_id is NOT NULL): a NEW sheet needs one selected in
  // the global project selector; an already-saved sheet keeps its own.
  const { selectedProject } = useProject();

  const initial = useRef(defaultSheet());
  const [columns, setColumns] = useState<TableColumn[]>(initial.current.columns);
  const [rows, setRows] = useState<TableRow[]>(initial.current.rows);
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editingTotal, setEditingTotal] = useState<string | null>(null);
  const [currentCellFormat, setCurrentCellFormat] = useState<'text' | 'checkbox'>('text');

  const [sheetId, setSheetId] = useState<string | null>(null);
  const [sheetName, setSheetName] = useState('');
  const [sheetProjectId, setSheetProjectId] = useState<string | null>(null);
  // what the server has (as JSON): the editor is "dirty" whenever it differs
  const [savedJson, setSavedJson] = useState(() => JSON.stringify({ columns: initial.current.columns, rows: initial.current.rows }));
  const [justSaved, setJustSaved] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const failedJsonRef = useRef('');
  const [confirmDialog, setConfirmDialog] = useState<{ title: string; message: string; confirmLabel: string; onConfirm: () => void } | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [nameDialog, setNameDialog] = useState<'save' | 'copy' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessingPDF, setIsProcessingPDF] = useState(false);
  const pdfInputRef = useRef<HTMLInputElement>(null);

  const currentJson = useMemo(() => JSON.stringify({ columns, rows }), [columns, rows]);
  const hasUnsavedChanges = currentJson !== savedJson;

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return;
      e.preventDefault();
      e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (hasUnsavedChanges) setJustSaved(false);
  }, [hasUnsavedChanges]);

  const { ref: scrollRef, edges, onScroll } = useScrollEdges(columns.length + rows.length);

  /** Runs `action`, first asking in-app when it would throw away work that is not on the server yet. */
  const guardUnsaved = useCallback((confirmLabel: string, action: () => void) => {
    if (!hasUnsavedChanges) action();
    else setConfirmDialog({ title: 'Unsaved changes', message: 'This Comparison Sheet has changes that are not saved yet. If you continue they will be lost.', confirmLabel, onConfirm: action });
  }, [hasUnsavedChanges]);

  // ---- editing ---------------------------------------------------------------------------------------------------
  const newCell = (rowId: string, columnId: string) => ({
    id: `cell-${rowId}-${columnId}`,
    value: '',
    type: currentCellFormat,
    checked: currentCellFormat === 'checkbox' ? false : undefined,
  });

  const addColumn = () => {
    const columnId = `col-${Date.now()}`;
    setColumns((prev) => [...prev, { id: columnId, subcontractorName: `Vendor ${prev.length + 1}`, total: '' }]);
    setRows((prev) => prev.map((row) => ({ ...row, cells: [...row.cells, newCell(row.id, columnId)] })));
  };

  const deleteColumn = (columnId: string) => {
    if (columns.length <= 1) return;
    const columnIndex = columns.findIndex((col) => col.id === columnId);
    setColumns((prev) => prev.filter((col) => col.id !== columnId));
    setRows((prev) => prev.map((row) => ({ ...row, cells: row.cells.filter((_, index) => index !== columnIndex) })));
  };

  const addRow = () => {
    const rowId = `row-${Date.now()}`;
    setRows((prev) => [...prev, { id: rowId, itemName: `Item ${prev.length + 1}`, cells: columns.map((col) => newCell(rowId, col.id)) }]);
  };

  const deleteRow = (rowId: string) => {
    if (rows.length > 1) setRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  const updateCell = (rowId: string, cellId: string, changes: { value?: string; checked?: boolean }) =>
    setRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, cells: row.cells.map((cell) => (cell.id === cellId ? { ...cell, ...changes } : cell)) } : row)));

  const updateColumn = (columnId: string, changes: Partial<TableColumn>) =>
    setColumns((prev) => prev.map((col) => (col.id === columnId ? { ...col, ...changes } : col)));

  const updateRowName = (rowId: string, name: string) => setRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, itemName: name } : row)));

  const setAllCellsFormat = (type: 'text' | 'checkbox') => {
    setCurrentCellFormat(type);
    setRows((prev) => prev.map((row) => ({ ...row, cells: row.cells.map((cell) => ({ ...cell, type, checked: type === 'checkbox' ? false : undefined })) })));
  };

  // ---- save / open -----------------------------------------------------------------------------------------------
  const resetSheet = () => {
    const fresh = defaultSheet();
    setColumns(fresh.columns);
    setRows(fresh.rows);
    setSavedJson(JSON.stringify({ columns: fresh.columns, rows: fresh.rows }));
    setSheetId(null);
    setSheetName('');
    setSheetProjectId(null);
    setError(null);
  };

  const applySheet = useCallback((sheet: ManualComparison) => {
    const loadedColumns = sheet.data.columns.map((col, index) => ({ id: col.id || `col-${index}`, subcontractorName: col.subcontractorName, total: col.total || '' }));
    const loadedRows: TableRow[] = sheet.data.rows.map((row, rowIndex) => ({
      id: `row-${rowIndex}`,
      itemName: typeof row[0] === 'string' ? row[0] : `Item ${rowIndex + 1}`,
      cells: row.slice(1).map((cell, cellIndex) => {
        const data = typeof cell === 'string' ? { value: cell, type: 'text' as const, checked: false } : cell;
        return { id: `cell-${rowIndex}-${cellIndex}`, value: data.value || '', type: data.type || 'text', checked: data.checked || false };
      }),
    }));
    setColumns(loadedColumns);
    setRows(loadedRows);
    setSavedJson(JSON.stringify({ columns: loadedColumns, rows: loadedRows }));
    setSheetId(sheet.id);
    setSheetName(sheet.data.name);
    setSheetProjectId(sheet.data.project_id || null);
    setCurrentCellFormat(loadedRows[0]?.cells[0]?.type ?? 'text');
  }, []);

  // A sheet picked in "Saved Sheets" is loaded here, once -- asking first if the sheet being edited has work the server
  // does not have yet (the editor stays mounted while other tabs are open, so that work is still here).
  const loadSheet = useCallback(async (id: string) => {
    setIsLoading(true);
    try {
      const sheet = await manualComparisonService.getComparison(id);
      if (sheet) applySheet(sheet);
      else setError('This Comparison Sheet no longer exists.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open the Comparison Sheet');
    } finally {
      setIsLoading(false);
      onSheetOpened?.();
    }
  }, [applySheet, onSheetOpened]);

  const latestOpen = useRef({ guardUnsaved, loadSheet, sheetId, onSheetOpened });
  latestOpen.current = { guardUnsaved, loadSheet, sheetId, onSheetOpened };
  useEffect(() => {
    if (!openSheetId) return;
    const { guardUnsaved: guard, loadSheet: load, sheetId: current, onSheetOpened: done } = latestOpen.current;
    if (current === openSheetId) {
      done?.(); // already the one being edited: keep the editor as it is
      return;
    }
    guard('Discard and open the other sheet', () => load(openSheetId));
    // asking: the request is consumed either way once the user answers (see the dialog's onCancel)
  }, [openSheetId]);

  const toRowsArray = () => rows.map((row) => [row.itemName, ...row.cells.map((cell) => ({ value: cell.value, type: cell.type, checked: cell.checked || false }))]);

  /**
   * Saves the sheet: updates the open one, or creates a new one under `name` (Save the first time / Save as new).
   * `auto` = the debounced autosave of an already-saved sheet: silent (its state lives in the status line, not in an
   * alert) and never asks for anything.
   */
  const save = async (name: string, asNew: boolean, auto = false) => {
    if (isSaving) return;
    const projectId = sheetProjectId || (selectedProject !== 'all' ? selectedProject : '');
    if (!projectId) {
      setError('Select a project before saving a Comparison Sheet.');
      return;
    }
    const jsonAtSave = currentJson;
    setIsSaving(true);
    setSaveFailed(false);
    if (!auto) setError(null);
    try {
      const payload = { name: name.trim(), columns, rows: toRowsArray(), notes: '' };
      const saved = sheetId && !asNew
        ? await manualComparisonService.updateComparison(sheetId, payload)
        : await manualComparisonService.createComparison({ ...payload, project_id: projectId });
      setSheetId(saved.id);
      setSheetName(saved.name);
      setSheetProjectId(projectId);
      // what was SAVED, not what is on screen now: edits made while saving stay "unsaved" and are saved next
      setSavedJson(jsonAtSave);
      setJustSaved(true);
      setRecovered(false);
      setNameDialog(null);
    } catch (err) {
      failedJsonRef.current = jsonAtSave;
      setSaveFailed(true);
      if (!auto) setError(err instanceof Error ? err.message : 'Failed to save the Comparison Sheet');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveClick = () => {
    if (sheetId) save(sheetName, false);
    else setNameDialog('save');
  };

  // ---- autosave -----------------------------------------------------------------------------------------------------
  // An already-saved sheet saves itself AUTOSAVE_MS after the last edit (the server only rewrites the columns that
  // changed). A brand-new sheet is never auto-created: it has no name yet -- it is protected by the local draft below.
  // After a failed save it waits for the next edit (or "Retry") instead of hammering the server.
  useEffect(() => {
    if (!sheetId || !hasUnsavedChanges || isSaving || currentJson === failedJsonRef.current) return;
    const timer = setTimeout(() => save(sheetName, false, true), AUTOSAVE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetId, hasUnsavedChanges, isSaving, currentJson]);

  // ---- local draft --------------------------------------------------------------------------------------------------
  // Whatever is not on the server yet is also kept in this browser, so leaving the page (sidebar link, reload, crash)
  // never loses it: it is restored the next time the sheet opens, and removed as soon as the server has it.
  const draftKey = companyId ? `${DRAFT_KEY_PREFIX}${companyId}` : null;
  const [recovered, setRecovered] = useState(false);
  const draftChecked = useRef(false);

  useEffect(() => {
    if (!draftKey || draftChecked.current) return;
    draftChecked.current = true;
    const draft = readDraft(draftKey);
    if (!draft) return;
    setColumns(draft.columns);
    setRows(draft.rows);
    setSheetId(draft.sheetId);
    setSheetName(draft.sheetName);
    setSheetProjectId(draft.sheetProjectId);
    setSavedJson(''); // the server does not have it: dirty (and, for a saved sheet, autosaved)
    setRecovered(true);
  }, [draftKey]);

  useEffect(() => {
    if (!draftKey || !draftChecked.current) return;
    if (!hasUnsavedChanges) {
      clearDraft(draftKey);
      return;
    }
    const timer = setTimeout(() => writeDraft(draftKey, { sheetId, sheetName, sheetProjectId, columns, rows }), 400);
    return () => clearTimeout(timer);
  }, [draftKey, hasUnsavedChanges, currentJson, sheetId, sheetName, sheetProjectId, columns, rows]);

  // dropping the draft: back to the server's copy of the sheet, or to an empty one
  const discardRecovered = async () => {
    setRecovered(false);
    if (draftKey) clearDraft(draftKey);
    if (sheetId) {
      const sheet = await manualComparisonService.getComparison(sheetId).catch(() => null);
      if (sheet) {
        applySheet(sheet);
        return;
      }
    }
    resetSheet();
  };

  // ---- PDF -> a new column with its line items ----------------------------------------------------------------------
  const addExtractedVendor = (extracted: ExtractedEstimateData) => {
    const stamp = Date.now();
    const isPristine =
      !hasUnsavedChanges && !sheetId && JSON.stringify(columns) === JSON.stringify(defaultSheet().columns) && rows.every((row) => row.cells.every((cell) => cell.value === ''));
    const column: TableColumn = { id: `col-${stamp}`, subcontractorName: extracted.vendor_name || 'New Vendor', total: extracted.estimate_total };
    const newRows: TableRow[] = extracted.line_items.map((item, index) => {
      const rowId = `row-${stamp}-${index}`;
      return { id: rowId, itemName: item, cells: [...(isPristine ? [] : columns), column].map((col) => newCell(rowId, col.id)) };
    });
    if (isPristine) {
      setColumns([column]);
      setRows(newRows);
    } else {
      setColumns((prev) => [...prev, column]);
      setRows((prev) => [...prev.map((row) => ({ ...row, cells: [...row.cells, newCell(row.id, column.id)] })), ...newRows]);
    }
  };

  const handlePDFUpload = async (file: File) => {
    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file.');
      return;
    }
    setIsProcessingPDF(true);
    setError(null);
    try {
      const pdf = await pdfAPIService.parsePDF(file);
      addExtractedVendor(await extractEstimateData(pdf.text));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process the PDF');
    } finally {
      setIsProcessingPDF(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    const pdf = files.find((file) => file.type === 'application/pdf');
    if (pdf) handlePDFUpload(pdf);
    else if (files.length > 0) setError('Please drop a PDF file. Other file types are not supported.');
  };

  // A new sheet needs a project; an open one has its own.
  if (!sheetId && selectedProject === 'all' && !isLoading && !openSheetId && !hasUnsavedChanges) {
    return (
      <div className="p-8 text-center border border-dashed border-gray-300 rounded-lg bg-gray-50">
        <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-700 font-medium">Select a project first</p>
        <p className="text-gray-500 text-sm mt-1">
          A Comparison Sheet belongs to a project. Choose one from the project selector above, or open a saved sheet from the Saved Sheets tab.
        </p>
      </div>
    );
  }

  const tableWidthRem = FIRST_COLUMN_REM + COLUMN_REM * columns.length;

  return (
    <div className="space-y-6">
      <input
        ref={pdfInputRef}
        type="file"
        accept=".pdf"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handlePDFUpload(file);
          e.target.value = '';
        }}
        className="hidden"
      />

      {error && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-start justify-between gap-3">
            <span>{error}</span>
            <button type="button" aria-label="Dismiss" onClick={() => setError(null)}><X className="h-4 w-4" /></button>
          </AlertDescription>
        </Alert>
      )}

      {recovered && (
        <Alert>
          <AlertDescription className="flex items-center justify-between gap-3">
            <span>We recovered your unsaved changes{sheetName ? ` to "${sheetName}"` : ' to a Comparison Sheet you had not saved yet'}.</span>
            <span className="flex items-center gap-3 shrink-0">
              <button type="button" className="underline" onClick={discardRecovered}>Discard</button>
              <button type="button" aria-label="Dismiss" onClick={() => setRecovered(false)}><X className="h-4 w-4" /></button>
            </span>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div>Comparison Sheet</div>
              {(sheetName || hasUnsavedChanges || justSaved || saveFailed) && (
                <div className="text-sm font-normal text-gray-500 break-words" role="status" aria-live="polite">
                  {sheetName && <span className="font-medium text-gray-700">{sheetName}</span>}
                  {sheetName && (hasUnsavedChanges || justSaved) && ' · '}
                  {isSaving ? 'Saving…' : saveFailed && hasUnsavedChanges ? (
                    <>
                      Couldn't save your changes ·{' '}
                      <button type="button" className="underline" onClick={() => save(sheetName, false)}>Retry</button>
                    </>
                  ) : hasUnsavedChanges ? 'Unsaved changes' : justSaved ? 'Saved' : ''}
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <StandardButton onClick={addColumn} customVariant="white">
                <Plus className="h-4 w-4 mr-2" />
                Add Column
              </StandardButton>
              <StandardButton onClick={addRow} customVariant="white">
                <Plus className="h-4 w-4 mr-2" />
                Add Row
              </StandardButton>
              <SecondaryButton onClick={handleSaveClick} disabled={isSaving || isLoading || (!!sheetId && !hasUnsavedChanges)}>
                {isSaving ? 'Saving...' : 'Save'}
              </SecondaryButton>
              <ThreeDotMenuButton
                items={[
                  { label: 'All Cells Text Input', icon: <Type className="h-4 w-4" />, onClick: () => setAllCellsFormat('text') },
                  { label: 'All Cells Checkboxes', icon: <CheckSquare className="h-4 w-4" />, onClick: () => setAllCellsFormat('checkbox') },
                  { label: isProcessingPDF ? 'Processing PDF...' : 'Upload PDF', icon: <Upload className="h-4 w-4" />, onClick: () => pdfInputRef.current?.click(), disabled: isProcessingPDF },
                  ...(sheetId ? [{ label: 'Save as New', icon: <Copy className="h-4 w-4" />, onClick: () => setNameDialog('copy') }] : []),
                  { label: 'New Sheet', icon: <FilePlus className="h-4 w-4" />, onClick: () => guardUnsaved('Discard and start over', resetSheet) },
                  { label: 'Clear', icon: <RotateCcw className="h-4 w-4" />, onClick: () => guardUnsaved('Discard and start over', resetSheet) },
                ]}
              />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className={`transition-colors duration-200 ${isDragOver ? 'bg-blue-50 border-2 border-dashed border-blue-400 rounded-lg p-4' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
            onDrop={handleDrop}
          >
            {isDragOver && <div className="text-center py-8 text-blue-600 font-medium">Drop the PDF bid here to extract its line items</div>}
            {isProcessingPDF && <div className="text-center py-8 text-gray-600 font-medium">Processing PDF... Please wait</div>}

            {/* The sheet scrolls horizontally on its own: any number of vendor columns stays reachable. */}
            <div className="relative rounded-lg" style={{ border: `1px solid ${colors.tableBorder}` }}>
              <div ref={scrollRef} onScroll={onScroll} className="overflow-x-auto">
                <table className="border-collapse bg-white" style={{ tableLayout: 'fixed', width: `${tableWidthRem}rem`, minWidth: '100%' }}>
                  <colgroup>
                    <col style={{ width: `${FIRST_COLUMN_REM}rem` }} />
                    {columns.map((column) => <col key={column.id} style={{ width: `${COLUMN_REM}rem` }} />)}
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="p-2 bg-gray-50 sticky left-0 z-10" style={{ border: `1px solid ${colors.tableBorder}` }}>
                        <span className="font-table-head">Items</span>
                      </th>
                      {columns.map((column) => (
                        <th key={column.id} className="group p-2 bg-gray-50 text-left" style={{ border: `1px solid ${colors.tableBorder}` }}>
                          <div className="space-y-1">
                            {editingColumn === column.id ? (
                              <Input
                                value={column.subcontractorName}
                                onChange={(e) => updateColumn(column.id, { subcontractorName: e.target.value })}
                                onBlur={() => setEditingColumn(null)}
                                onKeyDown={(e) => e.key === 'Enter' && setEditingColumn(null)}
                                className="h-8 font-small-form-input"
                                autoFocus
                              />
                            ) : (
                              <div className="flex items-center justify-between gap-1">
                                <span className="cursor-pointer flex-1 truncate font-small-form-label" title={column.subcontractorName} onClick={() => setEditingColumn(column.id)}>
                                  {column.subcontractorName}
                                </span>
                                <DeleteButton
                                  onClick={() => deleteColumn(column.id)}
                                  aria-label={`Delete column ${column.subcontractorName}`}
                                  className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                                  disabled={columns.length <= 1}
                                />
                              </div>
                            )}

                            {editingTotal === column.id ? (
                              <Input
                                value={column.total || ''}
                                onChange={(e) => updateColumn(column.id, { total: e.target.value })}
                                onBlur={() => setEditingTotal(null)}
                                onKeyDown={(e) => e.key === 'Enter' && setEditingTotal(null)}
                                className="h-6 font-small-form-input"
                                placeholder="Total amount"
                                autoFocus
                              />
                            ) : (
                              <div className="cursor-pointer font-small-form-label" onClick={() => setEditingTotal(column.id)}>
                                {column.total || 'Total'}
                              </div>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} className="group transition-colors hover:bg-blue-50">
                        <td className="p-2 bg-gray-50 group-hover:bg-blue-50 transition-colors sticky left-0 z-10" style={{ border: `1px solid ${colors.tableBorder}` }}>
                          {editingRow === row.id ? (
                            <Input
                              value={row.itemName}
                              onChange={(e) => updateRowName(row.id, e.target.value)}
                              onBlur={() => setEditingRow(null)}
                              onKeyDown={(e) => e.key === 'Enter' && setEditingRow(null)}
                              className="h-8 font-small-form-input"
                              autoFocus
                            />
                          ) : (
                            <div className="flex items-center justify-between gap-1">
                              <span className="cursor-pointer flex-1 truncate font-small-form-label" title={row.itemName} onClick={() => setEditingRow(row.id)}>
                                {row.itemName}
                              </span>
                              <DeleteButton
                                onClick={() => deleteRow(row.id)}
                                aria-label={`Delete row ${row.itemName}`}
                                className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                                disabled={rows.length <= 1}
                              />
                            </div>
                          )}
                        </td>
                        {row.cells.map((cell) => (
                          <td key={cell.id} className="p-1" style={{ border: `1px solid ${colors.tableBorder}` }}>
                            {cell.type === 'text' ? (
                              <Input value={cell.value} onChange={(e) => updateCell(row.id, cell.id, { value: e.target.value })} placeholder="Enter value" className="h-8" />
                            ) : (
                              <div className="flex items-center gap-2">
                                <input type="checkbox" checked={cell.checked || false} onChange={(e) => updateCell(row.id, cell.id, { checked: e.target.checked })} className="h-4 w-4" />
                                <Input value={cell.value} onChange={(e) => updateCell(row.id, cell.id, { value: e.target.value })} placeholder="Notes" className="h-8 flex-1" />
                              </div>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {edges.left && <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-black/10 to-transparent" />}
              {edges.right && <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-black/10 to-transparent" />}
            </div>

            {!isDragOver && !isProcessingPDF && (
              <div className="text-center py-2 text-gray-400 text-sm mt-2">
                💡 Drag and drop a PDF bid here to automatically extract its line items
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <GenericConfirmDialog
        isOpen={!!confirmDialog}
        title={confirmDialog?.title ?? ''}
        message={confirmDialog?.message ?? ''}
        confirmLabel={confirmDialog?.confirmLabel}
        onConfirm={() => {
          const pending = confirmDialog;
          setConfirmDialog(null);
          pending?.onConfirm();
        }}
        onCancel={() => {
          setConfirmDialog(null);
          onSheetOpened?.();
        }}
      />

      {nameDialog && (
        <GenericForm
          isOpen
          size="compact"
          mode="create"
          title="Comparison Sheet"
          headerTitle={nameDialog === 'copy' ? 'Save as New Comparison Sheet' : 'Save Comparison Sheet'}
          submitButtonText="Save"
          saving={isSaving}
          error={error}
          fields={SHEET_NAME_FIELDS}
          initialValues={{ name: nameDialog === 'copy' ? `${sheetName} (copy)` : sheetName }}
          onSubmit={(data) => save(data.name, nameDialog === 'copy')}
          onCancel={() => setNameDialog(null)}
        />
      )}
    </div>
  );
}
